import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Compact feedback widget with user context for testimonials
 * Non-intrusive, collapsible, keeps it light
 */
const FeedbackWidget = ({ analysisId, pageContext = 'results' }) => {
    const { user } = useAuth();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form state
    const [willingToPay, setWillingToPay] = useState(false);
    const [featureRequest, setFeatureRequest] = useState('');
    const [mostUseful, setMostUseful] = useState([]);
    const [overallRating, setOverallRating] = useState(0);
    const [comments, setComments] = useState('');

    // User context
    const [userStatus, setUserStatus] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [targetRole, setTargetRole] = useState('');
    const [collegeName, setCollegeName] = useState('');
    const [courseName, setCourseName] = useState('');
    const [yearsExperience, setYearsExperience] = useState('');

    // Consent
    const [consentTestimonial, setConsentTestimonial] = useState(false);
    const [consentWithDetails, setConsentWithDetails] = useState(false);

    // Options

    const usefulOptions = [
        'Easy to Use',
        'Resume score',
        'Job match %',
        'Missing skills',
        'Skills Gap Analysis',
        'Skill Development Roadmap',
        'Interview Prep Practice Questions & Answers',
        'Resume Tips',
        'Recommended AI Training Programs',
        'Skill Analysis and Progress (Quick Actions on Dashboard)'
    ];

    const statusOptions = [
        { value: 'employed', label: '💼 Employed' },
        { value: 'job_seeking', label: '🔍 Looking for job' },
        { value: 'student', label: '📚 Student' },
        { value: 'freelancer', label: '💻 Freelancer' }
    ];

    const experienceOptions = [
        { value: '0-1', label: '0-1 years' },
        { value: '1-3', label: '1-3 years' },
        { value: '3-5', label: '3-5 years' },
        { value: '5-10', label: '5-10 years' },
        { value: '10+', label: '10+ years' }
    ];

    const handleCheckbox = (value, currentArray, setArray) => {
        if (currentArray.includes(value)) {
            setArray(currentArray.filter(v => v !== value));
        } else {
            setArray([...currentArray, value]);
        }
    };

    const handleSubmit = async () => {
        if (!user) return;

        setIsSubmitting(true);
        try {
            const { error } = await supabase
                .from('user_feedback')
                .insert({
                    user_id: user.id,
                    analysis_id: analysisId || null,
                    would_pay_for: willingToPay ? ['Yes'] : null,
                    feature_request: featureRequest.trim() || null,
                    most_useful: mostUseful.length > 0 ? mostUseful : null,
                    overall_rating: overallRating || null,
                    comments: comments.trim() || null,
                    user_status: userStatus || null,
                    company_name: userStatus === 'employed' ? companyName.trim() || null : null,
                    target_role: userStatus === 'job_seeking' ? targetRole.trim() || null : null,
                    college_name: userStatus === 'student' ? collegeName.trim() || null : null,
                    course_name: userStatus === 'student' ? courseName.trim() || null : null,
                    years_experience: yearsExperience || null,
                    consent_testimonial: consentTestimonial,
                    consent_with_details: consentWithDetails,
                    page_context: pageContext
                });

            if (error) throw error;

            setIsSubmitted(true);
        } catch (error) {
            console.error('Feedback submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Already submitted
    if (isSubmitted) {
        return (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <span className="text-green-600 text-lg">✓</span>
                <p className="text-green-700 font-medium">Thanks for your feedback!</p>
                <p className="text-green-600 text-sm">It helps us build what you need</p>
            </div>
        );
    }

    // Collapsed state
    if (!isExpanded) {
        return (
            <div
                onClick={() => setIsExpanded(true)}
                className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">💬</span>
                        <div>
                            <p className="font-medium text-gray-800">Quick feedback? (30 sec)</p>
                            <p className="text-sm text-gray-600">Help us build what you need</p>
                        </div>
                    </div>
                    <span className="text-blue-500 text-xl">→</span>
                </div>
            </div>
        );
    }

    // Expanded form
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                    <span className="text-xl">💬</span>
                    <h3 className="text-lg font-semibold text-gray-800">Quick Feedback</h3>
                </div>
                <button
                    onClick={() => setIsExpanded(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                >
                    ✕
                </button>
            </div>

            {/* Star Rating */}
            <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">How useful was this?</p>
                <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            onClick={() => setOverallRating(star)}
                            className={`text-2xl transition-transform hover:scale-110 ${star <= overallRating ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                        >
                            ★
                        </button>
                    ))}
                </div>
            </div>

            {/* Most useful - compact chips */}
            <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">What's most useful?</p>
                <div className="flex flex-wrap gap-2">
                    {usefulOptions.map((option) => (
                        <button
                            key={option}
                            onClick={() => handleCheckbox(option, mostUseful, setMostUseful)}
                            className={`px-3 py-1 rounded-full text-sm transition-all ${mostUseful.includes(option)
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </div>

            {/* Willing to pay */}
            <div className="mb-4">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all">
                    <input
                        type="checkbox"
                        checked={willingToPay}
                        onChange={(e) => setWillingToPay(e.target.checked)}
                        className="w-5 h-5 rounded text-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                        Are you willing to pay for such a service?
                    </span>
                </label>
            </div>

            {/* What's missing */}
            <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">What feature is missing that you'd want?</p>
                <input
                    type="text"
                    value={featureRequest}
                    onChange={(e) => setFeatureRequest(e.target.value)}
                    placeholder="e.g., ATS compatibility check, salary insights..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                />
            </div>

            {/* User status - simple select */}
            <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Your current status (optional)</p>
                <div className="flex flex-wrap gap-2">
                    {statusOptions.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => setUserStatus(userStatus === option.value ? '' : option.value)}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${userStatus === option.value
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Dynamic fields based on status */}
            {userStatus === 'employed' && (
                <div className="mb-4 pl-2 border-l-2 border-purple-200">
                    <input
                        type="text"
                        placeholder="Company name (e.g., Deloitte, TCS)"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-purple-400 focus:outline-none"
                    />
                </div>
            )}

            {userStatus === 'job_seeking' && (
                <div className="mb-4 pl-2 border-l-2 border-purple-200">
                    <input
                        type="text"
                        placeholder="Target role (e.g., Software Engineer, Chartered Accountant, Data Scientist)"
                        value={targetRole}
                        onChange={(e) => setTargetRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-purple-400 focus:outline-none"
                    />
                </div>
            )}

            {userStatus === 'student' && (
                <div className="mb-4 pl-2 border-l-2 border-purple-200 space-y-2">
                    <input
                        type="text"
                        placeholder="College (e.g., IIT Delhi, BITS)"
                        value={collegeName}
                        onChange={(e) => setCollegeName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-purple-400 focus:outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Course (e.g., B.Tech CS, MBA)"
                        value={courseName}
                        onChange={(e) => setCourseName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-purple-400 focus:outline-none"
                    />
                </div>
            )}

            {/* Experience - only show if not student */}
            {userStatus && userStatus !== 'student' && (
                <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Years of experience</p>
                    <div className="flex flex-wrap gap-2">
                        {experienceOptions.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => setYearsExperience(yearsExperience === option.value ? '' : option.value)}
                                className={`px-3 py-1 rounded-full text-xs transition-all ${yearsExperience === option.value
                                    ? 'bg-gray-700 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Comment */}
            <div className="mb-4">
                <textarea
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Any other feedback? What's missing? Bugs? (optional)"
                    className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:border-blue-400 focus:outline-none"
                    rows={2}
                />
            </div>

            {/* Testimonial consent */}
            <div className="mb-5 p-3 bg-gray-50 rounded-lg space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={consentTestimonial}
                        onChange={(e) => {
                            setConsentTestimonial(e.target.checked);
                            if (!e.target.checked) setConsentWithDetails(false);
                        }}
                        className="mt-1 rounded"
                    />
                    <span className="text-sm text-gray-700">
                        You can use my feedback as a testimonial
                    </span>
                </label>

                {consentTestimonial && (userStatus === 'employed' || userStatus === 'student') && (
                    <label className="flex items-start gap-2 cursor-pointer ml-6">
                        <input
                            type="checkbox"
                            checked={consentWithDetails}
                            onChange={(e) => setConsentWithDetails(e.target.checked)}
                            className="mt-1 rounded"
                        />
                        <span className="text-xs text-gray-600">
                            Include my {userStatus === 'employed' ? 'company' : 'college'} name
                            <span className="text-gray-400"> (e.g., "User from {userStatus === 'employed' ? 'Google' : 'IIT'} found...")</span>
                        </span>
                    </label>
                )}
            </div>

            {/* Submit */}
            <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all disabled:opacity-50"
            >
                {isSubmitting ? 'Sending...' : 'Submit Feedback'}
            </button>
        </div>
    );
};

export default FeedbackWidget;
